import {
  AudioDelay,
  ControllerType,
  GameSettings,
} from '@/common/types/student';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import { getStudent } from '@/game/GameComponents/PhaserGame/GameInstance';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import { StudentManager } from '@/game/GameComponents/PhaserGame/Managers/StudentManager';
import SettingsPanel from '@/game/GameComponents/PhaserGame/Scenes/Settings/SettingsPanel';
import { SettingsPanelConfig } from '@/game/GameComponents/PhaserGame/Types/UITypes/SettingsPanelTypes';
import SettingElement from '@/game/GameComponents/PhaserGame/UI/SettingElement';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { ScrollGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/ScrollGroup';
import { ScrollGroupItem } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/ScrollGroupItem';
import {
  getAnchorPlugin,
  waitUntil,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import RadioButtonGroup from '@/game/GameComponents/PhaserGame/Utils/RadioButtonGroup';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';
import HUDButton from '@/game/prefabs/GeneralUI/Buttons/HUDButton';
import RadioButton from '@/game/prefabs/RadioButton/RadioButton';
import SettingToggle from '@/game/prefabs/SettingToggle/SettingToggle';
import { cloneDeep } from 'lodash';

export class SettingsPanelController {
  constructor(private settingsPanel: SettingsPanel) {
    this.updatedSettings = getStudent().settings;

    // Initialize close buttons.
    this.getResponsiveElements('closeButton').forEach(
      (closeButton: HUDButton) => {
        closeButton.submitSFX = 'panel-close';
        closeButton.setSubmissionCallback(() => {
          this.handleClose();
        });
      },
    );

    // Initialize submit buttons.
    this.getResponsiveElements('submitButton').forEach(
      (submitButton: DefaultButton) => {
        submitButton.controller.setSubmissionCallback(() => {
          this.handleClose();
        });
      },
    );

    // Populate the settings from the player object.
    const student = getStudent();

    this.updatedSettings = cloneDeep(student.settings);

    // Mobile scroll view.
    this.scrollGroup = new ScrollGroup({
      scene: settingsPanel,
      content: settingsPanel.settingContainer_m,
      scrollBacking: settingsPanel.scrollBarBacking_m,
      scrollElement: settingsPanel.scrollGroupBacking_m,
      scrollHandle: settingsPanel.scrollBarHandle_m,
      scrollSpeedX: 0,
    });

    // Articulatory gestures toggle.
    this.getResponsiveElements('articulatoryGesturesToggle').forEach(
      (articulatoryGesturesToggle: SettingToggle) => {
        articulatoryGesturesToggle.controller.settingElement?.setSetting(
          'articulatoryGestures',
          this.updatedSettings.articulatoryGesturesEnabled,
          'Articulatory Gestures',
          'Display mouth shapes for sounds as they are being spoken.',
          true,
        );
        articulatoryGesturesToggle.controller.addOnSelectionChangedCallback(
          (newSelection) =>
            this.articulatoryGesturesToggleOnSelectionChanged(newSelection),
        );
        // Create the scroll group item.
        new ScrollGroupItem(this.scrollGroup, [
          articulatoryGesturesToggle.interactionHitbox,
          articulatoryGesturesToggle.checkbox.checkboxBacking,
        ]);
      },
    );

    // Word segmentation toggle.
    this.getResponsiveElements('wordSegmentationToggle').forEach(
      (wordSegmentationToggle: SettingToggle) => {
        wordSegmentationToggle.controller.settingElement?.setSetting(
          'wordSegmenation',
          this.updatedSettings.wordSegmentationEnabled,
          'Sound Out Words',
          `Segment words and sentences to increase phonological reinforcement.`,
          true,
        );
        wordSegmentationToggle.controller.addOnSelectionChangedCallback(
          (newSelection) =>
            this.wordSegmentationToggleOnSelectionChanged(newSelection),
        );
        // Create the scroll group item.
        new ScrollGroupItem(this.scrollGroup, [
          wordSegmentationToggle.interactionHitbox,
          wordSegmentationToggle.checkbox.checkboxBacking,
        ]);
      },
    );

    // Phoneme name on card drop toggle.
    this.getResponsiveElements('phonemeNameOnCardDropToggle').forEach(
      (phonemeNameOnCardDropToggle: SettingToggle) => {
        phonemeNameOnCardDropToggle.controller.settingElement?.setSetting(
          'playPhonemeOnCardDrop',
          this.updatedSettings.playPhonemeOnCardDrop,
          'Play Phoneme on Card Drop',
          `When learner places a card, sound out that card's phoneme or morpheme.`,
          true,
        );
        phonemeNameOnCardDropToggle.controller.addOnSelectionChangedCallback(
          (newSelection) =>
            this.phonemeNameOnCardDropToggleOnSelectionChanged(newSelection),
        );
        // Create the scroll group item.
        new ScrollGroupItem(this.scrollGroup, [
          phonemeNameOnCardDropToggle.interactionHitbox,
          phonemeNameOnCardDropToggle.checkbox.checkboxBacking,
        ]);
      },
    );

    // Music enabled toggle.
    this.getResponsiveElements('musicEnabledToggle').forEach(
      (musicEnabledToggle: SettingToggle) => {
        musicEnabledToggle.controller.settingElement?.setSetting(
          'music',
          this.updatedSettings.audio.musicOn,
          'Music',
          undefined,
          true,
        );
        musicEnabledToggle.controller.addOnSelectionChangedCallback(
          (newSelection) =>
            this.musicEnabledToggleOnSelectionChanged(newSelection),
        );
        // Create the scroll group item.
        new ScrollGroupItem(this.scrollGroup, [
          musicEnabledToggle.interactionHitbox,
          musicEnabledToggle.checkbox.checkboxBacking,
        ]);
      },
    );

    // Sound enabled toggle.
    this.getResponsiveElements('soundEnabled').forEach(
      (soundEnabledToggle: SettingToggle) => {
        soundEnabledToggle.controller.settingElement?.setSetting(
          'sfx',
          this.updatedSettings.audio.sfxOn,
          'SFX',
          undefined,
          true,
        );
        soundEnabledToggle.controller.addOnSelectionChangedCallback(
          (newSelection) =>
            this.sfxEnabledToggleOnSelectionChanged(newSelection),
        );
        // Create the scroll group item.
        new ScrollGroupItem(this.scrollGroup, [
          soundEnabledToggle.interactionHitbox,
          soundEnabledToggle.checkbox.checkboxBacking,
        ]);
      },
    );

    // Correlate setting toggles.
    settingsPanel.musicEnabledToggle.controller.correlateWith(
      settingsPanel.musicEnabledToggle_m,
    );
    settingsPanel.soundEnabled.controller.correlateWith(
      settingsPanel.soundEnabled_m,
    );
    settingsPanel.articulatoryGesturesToggle.controller.correlateWith(
      settingsPanel.articulatoryGesturesToggle_m,
    );
    settingsPanel.phonemeNameOnCardDropToggle.controller.correlateWith(
      settingsPanel.phonemeNameOnCardDropToggle_m,
    );
    settingsPanel.wordSegmentationToggle.controller.correlateWith(
      settingsPanel.wordSegmentationToggle_m,
    );

    // Radio buttons.

    // Click to place radio button.
    this.getResponsiveElements('clickToPlace').forEach(
      (clickToPlace: RadioButton) => {
        clickToPlace.controller.settingElement?.setSetting(
          'clickToPlace',
          this.updatedSettings.controllerType === ControllerType.CLICK,
          'Click to Place',
          undefined,
          true,
        );
        clickToPlace.controller.onUserSelect = () =>
          this.handleClickToPlaceSelected();
        // Create the scroll group item.
        new ScrollGroupItem(this.scrollGroup, [
          clickToPlace.button,
          clickToPlace.interactionHitbox,
        ]);
      },
    );

    // Drag n drop radio button.
    this.getResponsiveElements('dragNDrop').forEach(
      (dragNDrop: RadioButton) => {
        dragNDrop.controller.settingElement?.setSetting(
          'dragNDrop',
          this.updatedSettings.controllerType === ControllerType.DRAG,
          'Drag & Drop',
          undefined,
          true,
        );
        dragNDrop.controller.onUserSelect = () =>
          this.handleDragNDropSelected();
        new ScrollGroupItem(this.scrollGroup, [
          dragNDrop.button,
          dragNDrop.interactionHitbox,
        ]);
      },
    );

    // Typing radio button.
    this.getResponsiveElements('typing').forEach((typing: RadioButton) => {
      typing.controller.settingElement?.setSetting(
        'keyboard',
        this.updatedSettings.controllerType === ControllerType.KEYBOARD,
        'Keyboard',
        undefined,
        true,
      );
      typing.controller.onUserSelect = () => this.handleTypingSelected();
      new ScrollGroupItem(this.scrollGroup, [
        typing.button,
        typing.interactionHitbox,
      ]);
    });

    // Short audio delay radio button.
    this.getResponsiveElements('short').forEach((short: RadioButton) => {
      short.controller.settingElement?.setSetting(
        'short',
        this.updatedSettings.audio.audioDelay === AudioDelay.SHORT,
        'Short',
        undefined,
        true,
      );
      short.controller.onUserSelect = () =>
        this.handleAudioDelayShortSelected();
      new ScrollGroupItem(this.scrollGroup, [
        short.button,
        short.interactionHitbox,
      ]);
    });

    // Medium audio delay radio button.
    this.getResponsiveElements('medium').forEach((medium: RadioButton) => {
      medium.controller.settingElement?.setSetting(
        'medium',
        this.updatedSettings.audio.audioDelay === AudioDelay.MEDIUM,
        'Medium',
        undefined,
        true,
      );
      medium.controller.onUserSelect = () =>
        this.handleAudioDelayMediumSelected();
      new ScrollGroupItem(this.scrollGroup, [
        medium.button,
        medium.interactionHitbox,
      ]);
    });

    // Long audio delay radio button.
    this.getResponsiveElements('long').forEach((long: RadioButton) => {
      long.controller.settingElement?.setSetting(
        'long',
        this.updatedSettings.audio.audioDelay === AudioDelay.LONG,
        'Long',
        undefined,
        true,
      );
      long.controller.onUserSelect = () => this.handleAudioDelayLongSelected();
      new ScrollGroupItem(this.scrollGroup, [
        long.button,
        long.interactionHitbox,
      ]);
    });

    // Radio button groups desktop.
    this.inputTypeRadioButtonGroup = new RadioButtonGroup({
      scene: settingsPanel,
      radioButtons: [
        {
          button: settingsPanel.clickToPlace,
          callback: () => this.handleClickToPlaceSelected(),
        },
        {
          button: settingsPanel.dragNDrop,
          callback: () => this.handleDragNDropSelected(),
        },
        {
          button: settingsPanel.typing,
          callback: () => this.handleTypingSelected(),
        },
      ],
    });
    this.segmentationDelayRadioButtonGroup = new RadioButtonGroup({
      scene: settingsPanel,
      radioButtons: [
        {
          button: settingsPanel.short,
          callback: () => this.handleAudioDelayShortSelected(),
        },
        {
          button: settingsPanel.medium,
          callback: () => this.handleAudioDelayMediumSelected(),
        },
        {
          button: settingsPanel.long,
          callback: () => this.handleAudioDelayLongSelected(),
        },
      ],
    });

    // Radio button groups mobile.
    this.inputTypeRadioButtonGroup_m = new RadioButtonGroup({
      scene: settingsPanel,
      radioButtons: [
        {
          button: settingsPanel.clickToPlace_m,
          callback: () => this.handleClickToPlaceSelected(),
        },
        {
          button: settingsPanel.dragNDrop_m,
          callback: () => this.handleDragNDropSelected(),
        },
        {
          button: settingsPanel.typing_m,
          callback: () => this.handleTypingSelected(),
        },
      ],
    });
    this.segmentationDelayRadioButtonGroup_m = new RadioButtonGroup({
      scene: settingsPanel,
      radioButtons: [
        {
          button: settingsPanel.short_m,
          callback: () => this.handleAudioDelayShortSelected(),
        },
        {
          button: settingsPanel.medium_m,
          callback: () => this.handleAudioDelayMediumSelected(),
        },
        {
          button: settingsPanel.long_m,
          callback: () => this.handleAudioDelayLongSelected(),
        },
      ],
    });

    // Correlate radio button groups.
    this.inputTypeRadioButtonGroup.correlateWith(
      this.inputTypeRadioButtonGroup_m,
    );
    this.segmentationDelayRadioButtonGroup.correlateWith(
      this.segmentationDelayRadioButtonGroup_m,
    );

    // Make initial selection on input type radio button group.
    const initialInputTypeSelection =
      this.getResponsiveButtonsFromControllerType(
        this.updatedSettings.controllerType,
      );
    const inputTypeRadioButtonGroups = [
      this.inputTypeRadioButtonGroup,
      this.inputTypeRadioButtonGroup_m,
    ];
    for (var i = 0; i < inputTypeRadioButtonGroups.length; i++) {
      inputTypeRadioButtonGroups[i]?.selectButton(initialInputTypeSelection[i]);
    }

    // Make initial selection on delay radio button group.
    const initialDelaySelection = this.getResponsiveButtonsFromDelay(
      this.updatedSettings.audio.audioDelay,
    );
    const delayRadioButtonGroups = [
      this.segmentationDelayRadioButtonGroup,
      this.segmentationDelayRadioButtonGroup_m,
    ];
    for (var i = 0; i < delayRadioButtonGroups.length; i++) {
      delayRadioButtonGroups[i]?.selectButton(initialDelaySelection[i]);
    }

    this.settingElements = [
      settingsPanel.articulatoryGesturesToggle.controller.settingElement,
      settingsPanel.wordSegmentationToggle.controller.settingElement,
      settingsPanel.phonemeNameOnCardDropToggle.controller.settingElement,
      settingsPanel.musicEnabledToggle.controller.settingElement,
      settingsPanel.soundEnabled.controller.settingElement,
      settingsPanel.clickToPlace.controller.settingElement,
      settingsPanel.dragNDrop.controller.settingElement,
      settingsPanel.typing.controller.settingElement,
      settingsPanel.short.controller.settingElement,
      settingsPanel.medium.controller.settingElement,
      settingsPanel.long.controller.settingElement,
    ];

    this.settingElements_m = [
      settingsPanel.articulatoryGesturesToggle_m.controller.settingElement,
      settingsPanel.wordSegmentationToggle_m.controller.settingElement,
      settingsPanel.phonemeNameOnCardDropToggle_m.controller.settingElement,
      settingsPanel.musicEnabledToggle_m.controller.settingElement,
      settingsPanel.soundEnabled_m.controller.settingElement,
      settingsPanel.clickToPlace_m.controller.settingElement,
      settingsPanel.dragNDrop_m.controller.settingElement,
      settingsPanel.typing_m.controller.settingElement,
      settingsPanel.short_m.controller.settingElement,
      settingsPanel.medium_m.controller.settingElement,
      settingsPanel.long_m.controller.settingElement,
    ];

    this.initializeResponsiveness();

    settingsPanel.events.on('resume', this.resume, this);
    settingsPanel.events.on('create', this.resume, this);
  }

  static openWithConfiguration(config: SettingsPanelConfig) {
    SettingsPanelController.config = config;
    EventBus.emit(GAME_EVENTS.SETTINGS_PANEL_OPENED);
  }

  static config: SettingsPanelConfig;

  scrollGroup: ScrollGroup;

  inputTypeRadioButtonGroup?: RadioButtonGroup;
  inputTypeRadioButtonGroup_m?: RadioButtonGroup;
  segmentationDelayRadioButtonGroup?: RadioButtonGroup;
  segmentationDelayRadioButtonGroup_m?: RadioButtonGroup;

  updatedSettings: GameSettings;
  settingElements: SettingElement[] = [];
  settingElements_m: SettingElement[] = [];

  getResponsiveElements(key: keyof SettingsPanel) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromScene(
      this.settingsPanel,
      key,
    );
  }

  resume() {
    this.refresh();
    this.configure(SettingsPanelController.config);
  }

  refresh() {
    this.updatedSettings = getStudent().settings;
    this.getResponsiveElements('musicEnabledToggle').forEach(
      (toggle: SettingToggle) => {
        toggle.controller.setSelected(this.updatedSettings.audio.musicOn);
      },
    );

    this.getResponsiveElements('soundEnabled').forEach(
      (toggle: SettingToggle) => {
        toggle.controller.setSelected(this.updatedSettings.audio.sfxOn);
      },
    );

    this.getResponsiveElements('clickToPlace').forEach(
      (toggle: SettingToggle) => {
        toggle.controller.setSelected(
          this.updatedSettings.controllerType === ControllerType.CLICK,
        );
      },
    );

    this.getResponsiveElements('dragNDrop').forEach((toggle: SettingToggle) => {
      toggle.controller.setSelected(
        this.updatedSettings.controllerType === ControllerType.DRAG,
      );
    });

    this.getResponsiveElements('typing').forEach((toggle: SettingToggle) => {
      toggle.controller.setSelected(
        this.updatedSettings.controllerType === ControllerType.KEYBOARD,
      );
    });

    this.getResponsiveElements('short').forEach((toggle: SettingToggle) => {
      toggle.controller.setSelected(
        this.updatedSettings.audio.audioDelay === AudioDelay.SHORT,
      );
    });

    this.getResponsiveElements('medium').forEach((toggle: SettingToggle) => {
      toggle.controller.setSelected(
        this.updatedSettings.audio.audioDelay === AudioDelay.MEDIUM,
      );
    });

    this.getResponsiveElements('long').forEach((toggle: SettingToggle) => {
      toggle.controller.setSelected(
        this.updatedSettings.audio.audioDelay === AudioDelay.LONG,
      );
    });

    this.getResponsiveElements('articulatoryGesturesToggle').forEach(
      (toggle: SettingToggle) => {
        toggle.controller.setSelected(
          this.updatedSettings.articulatoryGesturesEnabled,
        );
      },
    );

    this.getResponsiveElements('wordSegmentationToggle').forEach(
      (toggle: SettingToggle) => {
        toggle.controller.setSelected(
          this.updatedSettings.wordSegmentationEnabled,
        );
      },
    );

    this.getResponsiveElements('phonemeNameOnCardDropToggle').forEach(
      (toggle: SettingToggle) => {
        toggle.controller.setSelected(
          this.updatedSettings.playPhonemeOnCardDrop,
        );
      },
    );
  }

  configure(config: SettingsPanelConfig) {
    for (const settingElement of [
      ...this.settingElements,
      ...this.settingElements_m,
    ]) {
      const option = config.optionStatuses?.find(
        (option) => option.option === settingElement.key,
      );
      if (option) {
        settingElement.setSetting(
          option.option,
          option.value,
          settingElement.settingLabel.text,
          option.hoverMessage,
          option.enabled,
        );
      } else {
        // Make the setting elements enabled by default.
        settingElement.setSetting(
          settingElement.key,
          settingElement.element.controller.getIsSelected(),
          settingElement.settingLabel.text,
          undefined,
          true,
        );
      }
    }

    for (const option of config.optionStatuses ?? []) {
      const settingElements = this.getSettingElementsFromKey(option.option);
      settingElements.forEach((settingElement: SettingElement) => {
        settingElement.setSetting(
          option.option,
          option.value,
          settingElement.settingLabel.text,
          option.hoverMessage,
          option.enabled,
        );
      });
    }
  }

  articulatoryGesturesToggleOnSelectionChanged(newSelection: boolean) {
    this.updatedSettings = {
      ...this.updatedSettings,
      articulatoryGesturesEnabled: newSelection,
    };

    const { settingsPanel } = this;
    if (newSelection) {
      // Preload articulatory gestures.
      settingsPanel.load.pack(
        'articulatory-gestures',
        'game_assets/sprites/phaser_game/phaser_game_ui/articulatory-gestures/articulatory-gestures.json',
      );
      settingsPanel.load.start();
    }
  }

  wordSegmentationToggleOnSelectionChanged(newSelection: boolean) {
    this.updatedSettings = {
      ...this.updatedSettings,
      wordSegmentationEnabled: newSelection,
    };
  }

  phonemeNameOnCardDropToggleOnSelectionChanged(newSelection: boolean) {
    this.updatedSettings = {
      ...this.updatedSettings,
      playPhonemeOnCardDrop: newSelection,
    };
  }

  sfxEnabledToggleOnSelectionChanged(newSelection: boolean) {
    this.updatedSettings = {
      ...this.updatedSettings,
      audio: {
        ...this.updatedSettings.audio,
        sfxOn: newSelection,
      },
    };
    PhaserAudioManager.sfxEnabled = newSelection;
    // Force a sound to confirm to the player that sounds are re-enabled.
    if (newSelection) {
      PhaserAudioManager.playSFX(this.settingsPanel, 'submit-click');
    }
    EventBus.emit(GAME_EVENTS.SFX_ENABLED_TOGGLED, newSelection);
  }

  audioDelayChanged(newSelection: AudioDelay) {
    this.updatedSettings = {
      ...this.updatedSettings,
      audio: {
        ...this.updatedSettings.audio,
        audioDelay: newSelection,
      },
    };
  }

  inputMethodChanged(newSelection: ControllerType) {
    console.log('input method changed', newSelection);
    this.updatedSettings = {
      ...this.updatedSettings,
      controllerType: newSelection,
    };
    EventBus.emit(GAME_EVENTS.INPUT_METHOD_CHANGED, newSelection);
  }

  getSettingElementsFromKey(key: string): SettingElement[] {
    return [...this.settingElements, ...this.settingElements_m].filter(
      (element) => element.key === key,
    );
  }

  getResponsiveButtonsFromControllerType(
    controllerType: ControllerType,
  ): RadioButton[] {
    switch (controllerType) {
      case 'click':
        return this.getResponsiveElements('clickToPlace');
      case 'drag':
        return this.getResponsiveElements('dragNDrop');
      case 'keyboard':
        return this.getResponsiveElements('typing');
      default:
        return this.getResponsiveElements('clickToPlace');
    }
  }

  getResponsiveButtonsFromDelay(delay: number): RadioButton[] {
    switch (delay) {
      case 250:
        return this.getResponsiveElements('short');
      case 500:
        return this.getResponsiveElements('medium');
      case 1000:
        return this.getResponsiveElements('long');
      default:
        return this.getResponsiveElements('long');
    }
  }

  handleAudioDelayShortSelected() {
    this.audioDelayChanged(AudioDelay.SHORT);
  }

  handleAudioDelayMediumSelected() {
    this.audioDelayChanged(AudioDelay.MEDIUM);
  }

  handleAudioDelayLongSelected() {
    this.audioDelayChanged(AudioDelay.LONG);
  }

  musicEnabledToggleOnSelectionChanged(newSelection: boolean) {
    this.updatedSettings = {
      ...this.updatedSettings,
      audio: {
        ...this.updatedSettings.audio,
        musicOn: newSelection,
      },
    };
    PhaserAudioManager.musicEnabled = newSelection;
    EventBus.emit(GAME_EVENTS.MUSIC_TOGGLED, newSelection);
  }

  handleClickToPlaceSelected() {
    this.inputMethodChanged(ControllerType.CLICK);
  }

  handleDragNDropSelected() {
    this.inputMethodChanged(ControllerType.DRAG);
  }

  handleTypingSelected() {
    this.inputMethodChanged(ControllerType.KEYBOARD);
  }

  async handleClose() {
    const { settingsPanel } = this;
    settingsPanel.loadingPanel.setVisible(true);
    // Ensure the settings panel isn't still loading assets before it's allowed to close.
    await waitUntil(settingsPanel.time, () => !settingsPanel.load.isLoading());
    settingsPanel.loadingPanel.setVisible(false);

    getSceneManager()?.pauseScene('SettingsPanel');

    StudentManager.instance.updateSettings(this.updatedSettings);

    EventBus.emit(GAME_EVENTS.SETTINGS_PANEL_CLOSED);
  }

  handleResize() {
    const { settingsPanel } = this;
    if (!settingsPanel || !settingsPanel.scene.isActive()) return;
    const height = settingsPanel.game.scale.displaySize.height;
    const heightBreakpoint = 860;
    if (height < heightBreakpoint) {
      const ratio = height / heightBreakpoint;
      settingsPanel.mainContainer.scale = ratio;
    }
  }

  initializeResponsiveness() {
    const { settingsPanel } = this;

    // Initialize Breakpoints.
    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: settingsPanel,
      mobileView: settingsPanel.mobileView,
      desktopView: settingsPanel.desktopView,
    });

    // Initialize Responsiveness
    const anchor = getAnchorPlugin(settingsPanel);
    if (anchor) {
      anchor.add(settingsPanel.mainContainer, {
        centerX: '50%',
        centerY: '50%',
      });
      anchor.add(settingsPanel.mainContainer_m, {
        centerX: '50%',
        centerY: '50%',
      });
    }

    // Set max width on desktop setting toggles.
    const desktopMaxWidth = 200;
    this.settingElements.forEach((settingElement: SettingElement) => {
      if (settingElement instanceof SettingToggle) {
        settingElement.setMaxWidth(desktopMaxWidth);
      }
    });

    // Set max width on mobile setting toggles.
    const mobileMaxWidth = 192;
    this.settingElements_m.forEach((settingElement: SettingElement) => {
      if (settingElement.element instanceof SettingToggle) {
        settingElement.element.setMaxWidth(mobileMaxWidth);
      }
    });

    // Event handlers.
    ResponsivenessManager.InitializeResizeHandler(
      settingsPanel,
      () => this.handleResize(),
      this,
    );
  }
}
